package com.eladfinish.bible.contest.data.common

import io.github.aakira.napier.Napier
import kotlinx.browser.localStorage
import org.w3c.dom.get
import org.w3c.dom.set

/**
 * The app's private files on web, kept in the browser's `localStorage` — the only browser storage
 * with the synchronous access [FileStorage] promises. Each file is one entry, keyed
 * `file:<folder>/<name>`.
 *
 * `localStorage` holds a few megabytes per site; a write past that fails and is reported as
 * `false`, the same way a full disk is on the other platforms. The app's settings share this
 * storage, so "Reset preferences" (which clears the settings) removes these files as well.
 */
class WebFileStorage : FileStorage {

    override fun saveFile(folder: String, name: String, content: String): Boolean =
        try {
            localStorage[keyOf(folder, name)] = content
            true
        } catch (t: Throwable) {
            Napier.e(tag = TAG, message = "Can't save $folder/$name", throwable = t)
            false
        }

    override fun readFile(folder: String, name: String): String? =
        try {
            localStorage[keyOf(folder, name)]
        } catch (t: Throwable) {
            Napier.e(tag = TAG, message = "Can't read $folder/$name", throwable = t)
            null
        }

    override fun deleteFile(folder: String, name: String): Boolean {
        if (!fileExists(folder, name)) return false
        localStorage.removeItem(keyOf(folder, name))
        return true
    }

    override fun listFiles(folder: String): List<String> {
        val prefix = keyOf(folder, name = "")
        return allKeys().filter { it.startsWith(prefix) }.map { it.removePrefix(prefix) }
    }

    override fun renameFile(folder: String, oldName: String, newName: String): Boolean {
        val content = readFile(folder, oldName) ?: return false
        if (!saveFile(folder, newName, content)) return false
        if (oldName != newName) localStorage.removeItem(keyOf(folder, oldName))
        return true
    }

    override fun fileExists(folder: String, name: String): Boolean = readFile(folder, name) != null

    /** Removes every file, in every folder. */
    fun deleteAll() {
        allKeys().filter { it.startsWith(KEY_PREFIX) }.forEach { localStorage.removeItem(it) }
    }

    private fun allKeys(): List<String> =
        try {
            (0 until localStorage.length).mapNotNull { localStorage.key(it) }
        } catch (t: Throwable) {
            Napier.e(tag = TAG, message = "Can't list the stored files", throwable = t)
            emptyList()
        }

    private fun keyOf(folder: String, name: String): String = "$KEY_PREFIX$folder/$name"

    private companion object {
        const val TAG = "WebFileStorage"
        const val KEY_PREFIX = "file:"
    }
}

/**
 * A browser has no Documents folder a page can write to, list or change. Exporting a file is a
 * download: the browser saves it to the user's Downloads folder (or asks where), and from then on
 * it is out of the page's reach — hence nothing to list, read back, delete or rename.
 */
class WebDocumentsExporter : DocumentsExporter {

    override fun savePdfToDocuments(folder: String, name: String, data: ByteArray): String? =
        try {
            downloadBytesAsFile(
                bytes = data,
                fileName = "$name.${DocumentTextType.PDF.extension}",
                mimeType = DocumentTextType.PDF.mimeType,
            )
        } catch (t: Throwable) {
            Napier.e(tag = TAG, message = "Can't download $name", throwable = t)
            null
        }

    override fun saveTextFileToDocuments(
        folder: String,
        name: String,
        content: String,
        type: DocumentTextType,
    ): Boolean =
        try {
            downloadBytesAsFile(
                bytes = content.encodeToByteArray(),
                fileName = "$name.${type.extension}",
                mimeType = "${type.mimeType};charset=utf-8",
            )
            true
        } catch (t: Throwable) {
            Napier.e(tag = TAG, message = "Can't download $name", throwable = t)
            false
        }

    override fun listTextFilesInDocuments(folder: String, type: DocumentTextType): List<String> = emptyList()
    override fun readTextFileFromDocuments(folder: String, name: String, type: DocumentTextType): String? = null
    override fun deleteTextFileFromDocuments(folder: String, name: String, type: DocumentTextType): Boolean = false
    override fun renameTextFileInDocuments(
        folder: String,
        oldName: String,
        newName: String,
        type: DocumentTextType,
    ): Boolean = false

    private companion object {
        const val TAG = "WebDocumentsExporter"
    }
}
